/**
 * Replace backend mention markup `@[Display Name](uuid)` with a styled
 * inline <span> pill.
 *
 * Operates on already-sanitized HTML (DOMPurify output) — DOMPurify does
 * not touch the `@[...](...)` literal because it is plain text, so the
 * regex still matches after sanitize. We only emit a span with `class` and
 * `data-user-id`, both of which are DOMPurify-safe attributes, so the
 * resulting string can be passed straight to dangerouslySetInnerHTML.
 *
 * The regex matches the backend parser
 * (RequestRevisionComment::parseMentionsFromText) — anything rendered as a
 * pill is the same set of tokens the backend extracted as notification
 * targets when the comment was created.
 */

const MENTION_REGEX = /@\[([^\]]+)\]\(([a-fA-F0-9-]+)\)/gi;

// Both helpers assume the input is plain text (raw display name / UUID
// straight out of the markup match). A display name that literally contains
// an HTML entity string like `&lt;` would render as `&amp;lt;` — non-issue
// for current data shapes (display names come from `fullName`, no entities),
// but the rule is "feed plain text in".
const escapeAttr = (input: string): string =>
  input.replace(/&/g, '&amp;').replace(/"/g, '&quot;');

const escapeText = (input: string): string =>
  input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export const injectMentionPillsInHtml = (sanitizedHtml: string | null | undefined): string => {
  if (!sanitizedHtml) {
    return '';
  }

  return sanitizedHtml.replace(MENTION_REGEX, (_match, display: string, userId: string) => {
    return (
      `<span class="becreatives-mention" data-user-id="${escapeAttr(userId)}">` +
      `@${escapeText(display)}` +
      `</span>`
    );
  });
};

/**
 * Strip mention markup down to "@Display Name" for plain-text contexts
 * (clipboard, notifications, search hit previews). Leaves everything else
 * untouched.
 */
export const stripMentionsToPlainText = (rawText: string | null | undefined): string => {
  if (!rawText) {
    return '';
  }

  return rawText.replace(MENTION_REGEX, (_match, display: string) => `@${display}`);
};

/**
 * Translate a cursor position from the textarea's displayed plain text back
 * to the corresponding index in the underlying markup string.
 *
 * react-mentions stores `@[Display Name](uuid)` (markup) but renders
 * `@Display Name` (plain text) in the textarea. `textareaRef.selectionStart`
 * is in plain-text coordinates; without this mapping, slicing the markup at
 * that offset lands inside a mention token and any inserted text shreds it
 * — see Marius and Mantas review on PRs #184 / #965.
 *
 * Caret inside a mention's displayed text snaps to the END of the mention's
 * markup (post-`)`). Inserting BEFORE a mention is rare and ambiguous; end-
 * snap keeps the existing pill intact, which is the safer default.
 */
export const mapPlainCaretToMarkup = (markup: string, plainCaret: number): number => {
  if (!markup || plainCaret <= 0) {
    return Math.max(0, plainCaret);
  }

  let plainPos = 0;
  let markupPos = 0;
  // Use a fresh regex instance — MENTION_REGEX is shared module state with
  // the `g` flag, so reusing it concurrently would leave lastIndex stale.
  const re = /@\[([^\]]+)\]\(([a-fA-F0-9-]+)\)/gi;
  let match: RegExpExecArray | null;

  while ((match = re.exec(markup)) !== null) {
    const [fullMatch, display] = match;
    const nonMentionLen = match.index - markupPos;

    // Caret is somewhere in the non-mention run before this mention.
    if (plainCaret <= plainPos + nonMentionLen) {
      return markupPos + (plainCaret - plainPos);
    }
    plainPos += nonMentionLen;
    markupPos += nonMentionLen;

    // Caret falls inside the mention's displayed text. The displayTransform
    // is `@${display}` so plain length is 1 + display.length.
    const plainMentionLen = 1 + display.length;
    if (plainCaret < plainPos + plainMentionLen) {
      return markupPos + fullMatch.length;
    }
    plainPos += plainMentionLen;
    markupPos += fullMatch.length;
  }

  // Trailing non-mention run after the last mention.
  return markupPos + (plainCaret - plainPos);
};
